// API functions for points and leaderboard system

import { 
  UpdatePointsRequest,
  UpdatePointsResponse,
  GetPointsLeaderboardRequest,
  GetPointsLeaderboardResponse,
  AwardPointsRequest,
  AwardPointsResponse
} from '../../types/points';

/**
 * Update points for a couple (frontend API call)
 */
export async function updatePoints(request: UpdatePointsRequest): Promise<UpdatePointsResponse> {
  try {
    const response = await fetch('/.netlify/functions/updatePoints', {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify(request),
    });

    if (!response.ok) {
      throw new Error(`HTTP error! status: ${response.status}`);
    }

    const result: UpdatePointsResponse = await response.json();
    return result;
  } catch (error) {
    console.error('Error updating points:', error);
    return {
      success: false,
      error: error instanceof Error ? error.message : 'Failed to update points'
    };
  }
}

/**
 * Get points leaderboard data (frontend API call)
 */
export async function getPointsLeaderboard(request: GetPointsLeaderboardRequest): Promise<GetPointsLeaderboardResponse> {
  try {
    const response = await fetch('/.netlify/functions/getPointsLeaderboard', {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify(request),
    });

    if (!response.ok) {
      throw new Error(`HTTP error! status: ${response.status}`);
    }

    const result: GetPointsLeaderboardResponse = await response.json();
    return result;
  } catch (error) {
    console.error('Error fetching points leaderboard:', error);
    return {
      success: false,
      error: error instanceof Error ? error.message : 'Failed to fetch points leaderboard'
    };
  }
}

/**
 * Award points for specific activities (utility function)
 */
export async function awardPointsForActivity(
  couple_id: string,
  partner: 'partner1' | 'partner2',
  activity_type: string,
  points: number,
  description: string,
  related_id?: string,
  metadata?: any
): Promise<UpdatePointsResponse> {
  const request: UpdatePointsRequest = {
    couple_id,
    partner: partner as any,
    points_to_add: points,
    activity_type: activity_type as any,
    activity_description: description,
    related_id,
    metadata
  };

  return await updatePoints(request);
}

/**
 * Award points for expense addition
 */
export async function awardPointsForExpense(
  couple_id: string,
  partner: 'partner1' | 'partner2',
  expense_id: string,
  expense_amount: number,
  category_name?: string
): Promise<UpdatePointsResponse> {
  return await awardPointsForActivity(
    couple_id,
    partner,
    'expense_added',
    10, // Base points for expense
    'Added expense',
    expense_id,
    {
      expense_amount,
      category_name
    }
  );
}

/**
 * Award points for challenge completion
 */
export async function awardPointsForChallenge(
  couple_id: string,
  partner: 'partner1' | 'partner2',
  challenge_id: string,
  challenge_name: string,
  points_reward: number
): Promise<UpdatePointsResponse> {
  return await awardPointsForActivity(
    couple_id,
    partner,
    'challenge_completed',
    points_reward,
    `Completed challenge: ${challenge_name}`,
    challenge_id,
    {
      challenge_name
    }
  );
}

/**
 * Award points for achievement unlock
 */
export async function awardPointsForAchievement(
  couple_id: string,
  partner: 'partner1' | 'partner2',
  achievement_id: string,
  achievement_name: string,
  points_awarded: number
): Promise<UpdatePointsResponse> {
  return await awardPointsForActivity(
    couple_id,
    partner,
    'achievement_unlocked',
    points_awarded,
    `Unlocked achievement: ${achievement_name}`,
    achievement_id,
    {
      achievement_name
    }
  );
}

/**
 * Award points for streak milestone
 */
export async function awardPointsForStreak(
  couple_id: string,
  partner: 'partner1' | 'partner2',
  streak_days: number,
  points: number
): Promise<UpdatePointsResponse> {
  return await awardPointsForActivity(
    couple_id,
    partner,
    'streak_milestone',
    points,
    `Achieved ${streak_days}-day streak`,
    undefined,
    {
      streak_days
    }
  );
}

/**
 * Award points for category exploration
 */
export async function awardPointsForCategoryExploration(
  couple_id: string,
  partner: 'partner1' | 'partner2',
  category_name: string
): Promise<UpdatePointsResponse> {
  return await awardPointsForActivity(
    couple_id,
    partner,
    'category_explored',
    5, // Base points for category exploration
    `Explored category: ${category_name}`,
    undefined,
    {
      category_name
    }
  );
}

/**
 * Award points for budget goal achievement
 */
export async function awardPointsForBudgetGoal(
  couple_id: string,
  partner: 'partner1' | 'partner2'
): Promise<UpdatePointsResponse> {
  return await awardPointsForActivity(
    couple_id,
    partner,
    'budget_goal_met',
    100, // Base points for budget goal
    'Met budget goal'
  );
}

/**
 * Award weekly tracking bonus
 */
export async function awardWeeklyTrackingBonus(
  couple_id: string,
  partner: 'partner1' | 'partner2'
): Promise<UpdatePointsResponse> {
  return await awardPointsForActivity(
    couple_id,
    partner,
    'weekly_tracking',
    25, // Base points for weekly tracking
    'Weekly tracking bonus'
  );
}

/**
 * Award spending reduction bonus
 */
export async function awardSpendingReductionBonus(
  couple_id: string,
  partner: 'partner1' | 'partner2',
  reduction_percentage: number,
  points: number
): Promise<UpdatePointsResponse> {
  return await awardPointsForActivity(
    couple_id,
    partner,
    'spending_reduced',
    points,
    `Reduced spending by ${reduction_percentage.toFixed(1)}%`,
    undefined,
    {
      reduction_percentage
    }
  );
}

/**
 * Batch award points for multiple activities
 */
export async function batchAwardPoints(
  requests: UpdatePointsRequest[]
): Promise<UpdatePointsResponse[]> {
  const results: UpdatePointsResponse[] = [];
  
  // Process requests sequentially to avoid race conditions
  for (const request of requests) {
    const result = await updatePoints(request);
    results.push(result);
    
    // Add small delay to prevent overwhelming the API
    if (requests.length > 1) {
      await new Promise(resolve => setTimeout(resolve, 100));
    }
  }
  
  return results;
}

/**
 * Get current month points leaderboard
 */
export async function getCurrentMonthLeaderboard(couple_id: string): Promise<GetPointsLeaderboardResponse> {
  return await getPointsLeaderboard({
    couple_id,
    include_yearly_stats: false,
    activity_limit: 50
  });
}

/**
 * Get full leaderboard with yearly stats
 */
export async function getFullLeaderboard(couple_id: string): Promise<GetPointsLeaderboardResponse> {
  return await getPointsLeaderboard({
    couple_id,
    include_yearly_stats: true,
    activity_limit: 100
  });
}

/**
 * Check if API response indicates success
 */
export function isPointsResponseSuccess(response: UpdatePointsResponse | GetPointsLeaderboardResponse): boolean {
  return response.success === true;
}

/**
 * Extract error message from API response
 */
export function getPointsResponseError(response: UpdatePointsResponse | GetPointsLeaderboardResponse): string {
  return response.error || 'Unknown error occurred';
}

/**
 * Retry points update with exponential backoff
 */
export async function retryUpdatePoints(
  request: UpdatePointsRequest,
  maxRetries: number = 3,
  baseDelay: number = 1000
): Promise<UpdatePointsResponse> {
  let lastError: Error | null = null;
  
  for (let attempt = 0; attempt < maxRetries; attempt++) {
    try {
      const response = await updatePoints(request);
      
      if (response.success) {
        return response;
      } else {
        lastError = new Error(response.error || 'API returned error');
      }
    } catch (error) {
      lastError = error instanceof Error ? error : new Error('Unknown error');
    }
    
    // Wait before retrying (exponential backoff)
    if (attempt < maxRetries - 1) {
      const delay = baseDelay * Math.pow(2, attempt);
      await new Promise(resolve => setTimeout(resolve, delay));
    }
  }
  
  return {
    success: false,
    error: `Failed after ${maxRetries} attempts: ${lastError?.message || 'Unknown error'}`
  };
}